import type { RoutePoint } from '@/types/tour'

export type TrackRoutePointEntry = {
  routePoint: RoutePoint
  globalIndex: number
  trackIndex: number
}

export function requireTrackClientId(
  trackClientId: string | null | undefined,
  context = 'Route point',
): string {
  if (typeof trackClientId === 'string' && trackClientId.trim().length > 0) {
    return trackClientId
  }

  throw new Error(`${context} requires a trackClientId`)
}

export function normalizeTrackClientId(trackClientId?: string | null): string | null {
  return trackClientId ?? null
}

export function getTrackRoutePointEntries(
  routePoints: RoutePoint[] | null | undefined,
  trackClientId?: string | null,
): TrackRoutePointEntry[] {
  const normalizedTrackClientId = normalizeTrackClientId(trackClientId)
  const entries: TrackRoutePointEntry[] = []

  ;(routePoints ?? []).forEach((routePoint, globalIndex) => {
    if (normalizeTrackClientId(routePoint.trackClientId) !== normalizedTrackClientId) return
    entries.push({
      routePoint,
      globalIndex,
      trackIndex: entries.length,
    })
  })

  return entries
}

export function getTrackRoutePoints(
  routePoints: RoutePoint[] | null | undefined,
  trackClientId?: string | null,
): RoutePoint[] {
  return getTrackRoutePointEntries(routePoints, trackClientId).map((entry) => entry.routePoint)
}

export function getTrackLocalIndexFromGlobalIndex(
  routePoints: RoutePoint[] | null | undefined,
  trackClientId: string | null | undefined,
  globalIndex: number,
): number | null {
  const entries = getTrackRoutePointEntries(routePoints, trackClientId)
  const entry = entries.find((candidate) => candidate.globalIndex === globalIndex)
  return entry ? entry.trackIndex : null
}

export function getTrackGlobalInsertIndex(
  routePoints: RoutePoint[] | null | undefined,
  trackClientId: string | null | undefined,
  trackInsertIndex: number,
): number | null {
  const entries = getTrackRoutePointEntries(routePoints, trackClientId)
  if (trackInsertIndex < 0 || trackInsertIndex > entries.length) return null
  if (entries.length === 0) return (routePoints ?? []).length
  if (trackInsertIndex === entries.length) return entries[entries.length - 1].globalIndex + 1
  return entries[trackInsertIndex].globalIndex
}
